import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminArea() {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginSending, setLoginSending] = useState(false);

  useEffect(() => {
    if (location.state?.user) return;
    // CRITICAL: If returning from OAuth callback, skip the /me check — AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setAuth(null);
      return;
    }
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => {
        setUser(res.data);
        setAuth(true);
      })
      .catch(() => setAuth(false));
  }, [location.state]);

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(res.data);
    } catch {
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  useEffect(() => {
    if (auth === true) loadLeads();
  }, [auth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginEmail = async (e) => {
    e.preventDefault();
    if (loginSending) return;
    setLoginSending(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      setAuth(true);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoginSending(false);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setAuth(false);
      setUser(null);
      setLeads([]);
      navigate("/admin", { replace: true });
    }
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center font-sans text-sm text-[#5A564F]" data-testid="admin-loading">
        Verifica accesso...
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center px-6" data-testid="admin-login">
        <div className="bg-white border border-[#E3DFD7] p-10 max-w-md w-full text-center">
          <p className="font-editorial text-2xl text-[#1B4D3E] mb-2">Area Riservata</p>
          <p className="font-sans text-sm text-[#5A564F] mb-8">
            Accedi per vedere le richieste di analisi ricevute.
          </p>

          <form data-testid="email-login-form" onSubmit={loginEmail} className="space-y-3 text-left font-sans">
            <div>
              <label htmlFor="login-email" className="block text-xs font-bold uppercase text-[#5A564F] mb-1">Email</label>
              <input
                id="login-email"
                type="email"
                required
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                data-testid="login-input-email"
                className="w-full p-3 border border-[#D1E7DD] text-sm focus:outline-none focus:border-[#1B4D3E]"
                placeholder="la-tua-email@esempio.it"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="block text-xs font-bold uppercase text-[#5A564F] mb-1">Password</label>
              <input
                id="login-password"
                type="password"
                required
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                data-testid="login-input-password"
                className="w-full p-3 border border-[#D1E7DD] text-sm focus:outline-none focus:border-[#1B4D3E]"
                placeholder="••••••••"
              />
            </div>
            {loginError && (
              <p data-testid="login-error-message" className="text-xs text-red-700 bg-red-50 border border-red-200 p-2">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loginSending}
              data-testid="email-login-button"
              className="w-full py-4 bg-[#1B4D3E] text-white font-bold text-xs uppercase tracking-widest hover:bg-[#12352B] transition-colors disabled:opacity-60"
            >
              {loginSending ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <span className="flex-1 border-t border-[#E3DFD7]" />
            <span className="font-sans text-[10px] uppercase tracking-widest text-[#736F67]">oppure</span>
            <span className="flex-1 border-t border-[#E3DFD7]" />
          </div>

          <button
            onClick={login}
            data-testid="google-login-button"
            className="w-full py-4 border border-[#1B4D3E] text-[#1B4D3E] font-sans font-bold text-xs uppercase tracking-widest hover:bg-[#1B4D3E] hover:text-white transition-colors"
          >
            Accedi con Google
          </button>
          <a href="/" data-testid="back-to-site-link" className="block mt-6 font-sans text-xs text-[#736F67] underline underline-offset-4">
            Torna al sito
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F2] px-6 py-10" data-testid="admin-dashboard">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
          <div>
            <p className="font-editorial text-3xl text-[#1B4D3E]">Richieste di analisi</p>
            <p className="font-sans text-xs text-[#736F67] mt-1" data-testid="admin-user-email">
              Accesso come {user?.name || user?.email}
            </p>
          </div>
          <div className="flex gap-3 font-sans text-xs font-bold uppercase tracking-wider">
            <button
              onClick={loadLeads}
              data-testid="refresh-leads-button"
              className="px-4 py-2 border border-[#1B4D3E] text-[#1B4D3E] hover:bg-[#1B4D3E] hover:text-white transition-colors flex items-center gap-2"
            >
              <RefreshCw size={14} /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="px-4 py-2 bg-[#1B4D3E] text-white hover:bg-[#12352B] transition-colors flex items-center gap-2"
            >
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>

        {loadingLeads ? (
          <p className="font-sans text-sm text-[#5A564F]" data-testid="leads-loading">Caricamento richieste...</p>
        ) : leads.length === 0 ? (
          <div className="bg-white border border-[#E3DFD7] p-10 text-center" data-testid="leads-empty">
            <p className="font-editorial text-xl text-[#1B4D3E] mb-2">Nessuna richiesta ancora.</p>
            <p className="font-sans text-sm text-[#5A564F]">Le richieste inviate dal modulo del sito compariranno qui.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="leads-list">
            {leads.map((lead) => (
              <div key={lead.id} data-testid={`lead-card-${lead.id}`} className="bg-white border border-[#E3DFD7] p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <p className="font-editorial text-lg text-[#1B4D3E]">{lead.nominativo}</p>
                  <p className="font-sans text-sm text-[#5A564F]">{lead.attivita || "Settore non indicato"}</p>
                </div>
                <div className="font-sans text-sm text-right">
                  <a href={`tel:+39${lead.telefono.replace(/\s/g, "")}`} data-testid={`lead-phone-${lead.id}`} className="text-[#1B4D3E] font-bold hover:underline">
                    {lead.telefono}
                  </a>
                  <p className="text-xs text-[#736F67] mt-1">
                    {new Date(lead.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
